import { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { useToast } from '../Toast';
import { pushSupported, isStandalone, enablePush } from '../push';

// First-run soft prompt for notifications. Shows only inside the INSTALLED app
// (iOS requires the installed PWA, and requires the real permission prompt to be
// fired from a tap — which the Enable button provides). We never call
// requestPermission() automatically: burning the one-shot OS permission on an
// unprompted popup is the worst outcome. "Not now" re-asks after 7 days; once the
// OS permission is granted or denied we stop soft-prompting entirely.
const KEY = 'ology-push-prompt';
const REASK_MS = 7 * 24 * 60 * 60 * 1000;

export default function PushPrompt() {
  const toast = useToast();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    try {
      if (!pushSupported() || !isStandalone()) return;      // installed app only
      if (Notification.permission !== 'default') return;    // already granted/denied → don't soft-prompt
      const last = parseInt(localStorage.getItem(KEY) || '0', 10);
      if (last && Date.now() - last < REASK_MS) return;     // dismissed within the last 7 days
      const t = setTimeout(() => setShow(true), 700);        // let first paint settle
      return () => clearTimeout(t);
    } catch { /* Notification may be absent */ }
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      await enablePush();                                    // fires the real OS prompt (within this tap)
      toast.success('Notifications are on for this device.');
      setShow(false);
    } catch (e) {
      toast.error(e.message);
      localStorage.setItem(KEY, String(Date.now()));        // denied/failed → back off
      setShow(false);
    } finally { setBusy(false); }
  };

  const later = () => {
    localStorage.setItem(KEY, String(Date.now()));
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 p-4 sm:p-6 flex justify-center pointer-events-none">
      <div className="pointer-events-auto w-full max-w-md bg-gray-800 border border-gray-700 rounded-2xl shadow-2xl p-5"
        style={{ animation: 'fadeIn 0.2s ease' }}>
        <div className="flex items-start gap-3">
          <div className="shrink-0 rounded-xl p-2" style={{ backgroundColor: 'rgba(240,90,40,0.15)' }}>
            <Bell size={22} style={{ color: '#F05A28' }} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-white font-semibold text-base">Turn on notifications?</h3>
            <p className="text-gray-400 text-sm mt-0.5">
              Get a heads-up when something needs your attention. You choose exactly what — tune it anytime in Notifications → Settings.
            </p>
          </div>
          <button onClick={later} aria-label="Dismiss" className="text-gray-500 hover:text-gray-300 -mt-1 -mr-1 p-1">
            <X size={18} />
          </button>
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={enable} disabled={busy}
            className="flex-1 py-2.5 rounded-lg text-white text-sm font-medium disabled:opacity-60"
            style={{ backgroundColor: '#F05A28' }}>
            {busy ? 'Working…' : 'Enable'}
          </button>
          <button onClick={later} disabled={busy}
            className="px-4 py-2.5 rounded-lg text-sm font-medium bg-gray-700 text-gray-200 hover:opacity-80">
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}
